// src/components/CategoryManagement.js
import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Button, Grid, Card, CardMedia,
    CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, Snackbar, Alert
} from '@mui/material';
import { Upload, Edit, Trash } from 'lucide-react';
import api from '../utils/api';
import UploadProgressDialog from './UploadProgressDialog';
import { DEFAULT_PLACEHOLDER } from './LazyImage';

const CategoryManagement = () => {
    const [categories, setCategories] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [selectedFile, setSelectedFile] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Upload progress states
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [currentFile, setCurrentFile] = useState('');

    const userRole = localStorage.getItem('userRole');

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data);
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to fetch categories',
                severity: 'error'
            });
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                setSelectedFile(file);
            } else {
                setSnackbar({
                    open: true,
                    message: 'Please select an image file',
                    severity: 'error'
                });
            }
        }
    };

    const handleSubmit = async () => {
        try {
            let categoryId = selectedCategory?._id;

            if (!categoryId) {
                // Create new category
                const response = await api.post('/categories', formData);
                categoryId = response.data._id;
            } else {
                // Update existing category
                await api.put(`/categories/${categoryId}`, formData);
            }

            // Upload image if selected
            if (selectedFile) {
                const formDataToSend = new FormData();
                formDataToSend.append('image', selectedFile);

                setIsUploading(true);
                setCurrentFile(selectedFile.name);
                setUploadProgress(0);

                await api.post(`/categories/${categoryId}/image`, formDataToSend, {
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    }
                });

                setIsUploading(false);
            }

            setSnackbar({
                open: true,
                message: `Category ${selectedCategory ? 'updated' : 'created'} successfully`,
                severity: 'success'
            });

            handleCloseDialog();
            fetchCategories();
        } catch (error) {
            setIsUploading(false);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to save category',
                severity: 'error'
            });
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete(`/categories/${selectedCategory._id}`);
            setSnackbar({
                open: true,
                message: 'Category deleted successfully',
                severity: 'success'
            });
            setConfirmDelete(false);
            setSelectedCategory(null);
            fetchCategories();
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to delete category',
                severity: 'error'
            });
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedCategory(null);
        setFormData({ name: '', description: '' });
        setSelectedFile(null);
    };

    const handleEdit = (category) => {
        setSelectedCategory(category);
        setFormData({
            name: category.name,
            description: category.description || ''
        });
        setOpenDialog(true);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}
                >
                    Category Management
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Organize your products by creating and managing categories with images
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenDialog(true)}
                    startIcon={<Upload />}
                    sx={{
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                        }
                    }}
                >
                    Add New Category
                </Button>
            </Box>

            <Grid container spacing={3}>
                {categories.map(category => (
                    <Grid item xs={12} sm={6} md={4} key={category._id}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                                    transform: 'translateY(-4px)',
                                }
                            }}
                        >
                            <CardMedia
                                component="img"
                                height="200"
                                image={category.image?.url || DEFAULT_PLACEHOLDER}
                                alt={category.name}
                                sx={{ height: 200, objectFit: 'cover' }}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = DEFAULT_PLACEHOLDER;
                                }}
                            />
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {category.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {category.description}
                                </Typography>
                                {userRole === 'owner' && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                                        <IconButton
                                            onClick={() => handleEdit(category)}
                                            sx={{
                                                color: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.light', color: 'white' }
                                            }}
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => {
                                                setSelectedCategory(category);
                                                setConfirmDelete(true);
                                            }}
                                            sx={{
                                                color: 'error.main',
                                                '&:hover': { bgcolor: 'error.light', color: 'white' }
                                            }}
                                        >
                                            <Trash />
                                        </IconButton>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Category Form Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600 }}>
                    {selectedCategory ? 'Edit Category' : 'Add New Category'}
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label="Category Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        onFocus={(e) => e.target.select()}
                        margin="normal"
                        multiline
                        rows={3}
                    />
                    <Box sx={{ mt: 2 }}>
                        {selectedCategory?.image?.url && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                    Current Image
                                </Typography>
                                <img
                                    src={selectedCategory.image.url}
                                    alt="Current category"
                                    style={{ maxHeight: '160px', objectFit: 'contain' }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = DEFAULT_PLACEHOLDER;
                                    }}
                                />
                            </Box>
                        )}
                        <input
                            accept="image/*"
                            type="file"
                            id="category-image"
                            hidden
                            onChange={handleFileSelect}
                        />
                        <label htmlFor="category-image">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<Upload />}
                                sx={{ textTransform: 'none' }}
                            >
                                {selectedFile ? selectedFile.name : 'Upload Image'}
                            </Button>
                        </label>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 600
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this category? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upload Progress Dialog */}
            <UploadProgressDialog
                open={isUploading}
                progress={uploadProgress}
                fileName={currentFile}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CategoryManagement;